import React, { useMemo } from 'react'
import moment from 'moment'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import AccommodationSameRoomOptionTile from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTile'
import AccommodationSameRoomOptionTileDetails from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileDetails'
import AccommodationSameRoomOptionTilePricing from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTilePricing'
import AccommodationSameRoomOptionTileCTA from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileCTA'
import FlashHotelOptionPriceDetails from 'components/OfferPage/FlashHotel/FlashHotelOptionPriceDetails'
import FlashHotelOptionCTAs from 'components/OfferPage/FlashHotel/FlashHotelOptionCTAs'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import InclusionsSection from 'components/OfferPage/FlashHotel/InclusionsSection'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import useLoyaltyOfferPageTargetStayUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageTargetStayUpgrade'
import useLoyaltyTargetUpgradePackages from 'loyalty/hooks/useLoyaltyTargetUpgradePackages'
import useLoyaltyOfferPageClearTargetStayUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageClearTargetStayUpgrade'
import OfferPageLoyaltyStayUpgradeCallOutBanner from 'components/OfferPage/Loyalty/OfferPageLoyaltyStayUpgradeCallOutBanner'
import { useAppSelector } from 'hooks/reduxHooks'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import { AccommodationSameRoomOptionTileSubgrid } from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileSubgrid'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import SolidStarIcon from 'LuxKit/Icons/solid/SolidStarIcon'
import { useIntl } from 'react-intl'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import isTasteOfGoldEligibleOffer from 'takeovers/tasteOfGoldTakeover/utils/isTasteOfGoldEligibleOffer'
import TasteOfGoldTileBenefitMessage from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldTileBenefitMessage'
import { mediaQueryUp } from 'components/utils/breakpoint'

const INCLUSIONS_COLLAPSED_MAX_HEIGHT = 80

const InclusionsContainer = styled.div`
  display: grid;
  grid-template-areas: 'base-package' 'target-upgrade-package';
  grid-template-rows: 1fr;
`

const BasePackageInclusions = styled.div`
  grid-area: base-package;
  grid-row: 1;
  opacity: 1;
  transform: translateY(0);
  transition: opacity 300ms ease, transform 300ms ease;
  transition-delay: 300ms;

  &:not(.show) {
    opacity: 0;
    transform: translateY(${rem(16)});
    transition-delay: 0ms;
    pointer-events: none;
  }
`

const TargetUpgradePackageInclusions = styled.div`
  grid-area: target-upgrade-package;
  grid-row: 1;
  opacity: 1;
  transform: translateY(${rem(0)});
  transition: opacity 300ms ease, transform 300ms ease;
  transition-delay: 300ms;

  &:not(.show) {
    opacity: 0;
    transform: translateY(${rem(-16)});
    transition-delay: 0ms;
    pointer-events: none;
  }
`

const SuggestedPackageBannerContainer = styled.div`
  ${mediaQueryUp.tablet} {
    border-inline: 1px solid ${props => props.theme.palette.neutral.default.five};
  }
`

interface Props {
  offer: App.HotelOffer
  packageView: App.OfferPackageOptionView
  packagePosition?: number
  checkIn?: string
  duration: number
  availableForPurchase?: boolean
  roomsBeforeSurcharge?: number
  inclusionsOpen?: boolean
  onInclusionsToggle?: () => void
  suggestedPackageMessage?: string
  /**
   * Custom content to render in the pricing area.
   * If provided, replaces the default FlashHotelOptionPriceDetails.
   */
  children?: React.ReactNode
}

function FlashHotelRateOption(props: Props) {
  const {
    offer,
    packageView,
    packagePosition,
    checkIn,
    duration,
    availableForPurchase,
    roomsBeforeSurcharge,
    children,
    inclusionsOpen,
    onInclusionsToggle,
    suggestedPackageMessage,
  } = props

  const intl = useIntl()
  const pkg = packageView.package

  const viewMoreInclusionsText = intl.formatMessage({
    id: 'H4ATJCPhDL',
    defaultMessage: 'View more inclusions',
    description: 'Text to show more inclusions',
  })

  const viewLessInclusionsText = intl.formatMessage({
    id: 'v3fu9Hcksv',
    defaultMessage: 'View less inclusions',
    description: 'Text to show less inclusions',
  })
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const checkOutMoment = useMemo(() => checkInMoment?.clone().add(duration, 'days'), [checkInMoment, duration])

  const { inclusions, bonusInclusions, luxPlusInclusions } = useOfferInclusions(pkg, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const {
    selectedTargetUpgradePackage,
    selectedTargetUpgradeType,
  } = useLoyaltyOfferPageTargetStayUpgrade(pkg)

  const targetUpgradePackages = useLoyaltyTargetUpgradePackages(pkg, offer)
  const loyaltyRoomUpgradePkg = targetUpgradePackages?.room
  const loyaltyPackageUpgradePkg = targetUpgradePackages?.package

  const {
    inclusions: loyaltyRoomUpgradeInclusions,
    bonusInclusions: loyaltyRoomUpgradeBonusInclusions,
    luxPlusInclusions: loyaltyRoomUpgradeLuxPlusInclusions,
  } = useOfferInclusions(loyaltyRoomUpgradePkg, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const {
    inclusions: loyaltyPackageUpgradeInclusions,
    bonusInclusions: loyaltyPackageUpgradeBonusInclusions,
    luxPlusInclusions: loyaltyPackageUpgradeLuxPlusInclusions,
  } = useOfferInclusions(loyaltyPackageUpgradePkg, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const hasInclusions = useMemo(() => {
    return luxPlusInclusions.length > 0 || inclusions.length > 0 || bonusInclusions.length > 0
  }, [luxPlusInclusions, inclusions, bonusInclusions])

  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const isLoyaltyRoomUpgradesAvailable = useUserHasLoyaltyBenefit('hotel_upgrade')

  const currency = useAppSelector(state => state.geo.currentCurrency)
  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)
  const showTasteOfGoldBenefit = useMemo(() => {
    return isTasteOfGoldActive && isTasteOfGoldEligibleOffer(
      offer,
      currency,
      offer.property?.geoData?.countryCode?.toLowerCase() !== 'au',
      pkg.price,
    )
  }, [isTasteOfGoldActive, offer, currency, pkg.price])

  useLoyaltyOfferPageClearTargetStayUpgrade(packageView)

  return (
    <AccommodationSameRoomOptionTileSubgrid>
      {suggestedPackageMessage && <SuggestedPackageBannerContainer>
        <MessageBanner kind="promotional-primary" icon={<SolidStarIcon />}>{suggestedPackageMessage}</MessageBanner>
      </SuggestedPackageBannerContainer>}
      <AccommodationSameRoomOptionTile>
        <AccommodationSameRoomOptionTileDetails
          packageNumber={packagePosition}
          optionName={pkg.name}
          selectedLoyaltyTargetUpgradePackage={selectedTargetUpgradePackage}
        >
          {availableForPurchase && !selectedTargetUpgradePackage && pkg.roomRate && (
            <FlashCancellationPolicies
              checkInDate={checkIn}
              propertyTimezoneOffset={offer.property?.timezoneOffset}
              cancellationPolicy={pkg.roomRate.cancellationPolicy}
            />
          )}
          {availableForPurchase && selectedTargetUpgradePackage?.roomRate && (
            <FlashCancellationPolicies
              checkInDate={checkIn}
              propertyTimezoneOffset={offer.property?.timezoneOffset}
              cancellationPolicy={selectedTargetUpgradePackage.roomRate.cancellationPolicy}
            />
          )}
          {isLoyaltyEnabled && isLoyaltyRoomUpgradesAvailable && (
            <OfferPageLoyaltyStayUpgradeCallOutBanner packageView={packageView} />
          )}
          {hasInclusions && (
            <InclusionsContainer>
              <BasePackageInclusions className={clsx({ 'show': !selectedTargetUpgradePackage })}>
                <LargeContentToggle
                  maxHeight={INCLUSIONS_COLLAPSED_MAX_HEIGHT}
                  showMoreText={viewMoreInclusionsText}
                  showLessText={viewLessInclusionsText}
                  open={inclusionsOpen}
                  onToggle={onInclusionsToggle}
                >
                  <VerticalSpacer gap={8}>
                    {!!luxPlusInclusions.length && (
                      <LuxPlusInclusions
                        luxPlusInclusions={luxPlusInclusions}
                        accommodationOffer={offer}
                        hideUpsellModal
                      />
                    )}
                    {showTasteOfGoldBenefit && <TasteOfGoldTileBenefitMessage />}
                    <InclusionsSection inclusions={inclusions} bonusInclusions={bonusInclusions} />
                  </VerticalSpacer>
                </LargeContentToggle>
              </BasePackageInclusions>
              {loyaltyRoomUpgradePkg && (
                <TargetUpgradePackageInclusions className={clsx({ 'show': selectedTargetUpgradeType === 'room' })}>
                  <LargeContentToggle
                    maxHeight={INCLUSIONS_COLLAPSED_MAX_HEIGHT}
                    showMoreText={viewMoreInclusionsText}
                    showLessText={viewLessInclusionsText}
                    open={inclusionsOpen}
                    onToggle={onInclusionsToggle}
                  >
                    <VerticalSpacer gap={8}>
                      {!!loyaltyRoomUpgradeLuxPlusInclusions.length && (
                        <LuxPlusInclusions
                          luxPlusInclusions={loyaltyRoomUpgradeLuxPlusInclusions}
                          accommodationOffer={offer}
                          hideUpsellModal
                        />
                      )}
                      {showTasteOfGoldBenefit && <TasteOfGoldTileBenefitMessage />}
                      <InclusionsSection inclusions={loyaltyRoomUpgradeInclusions} bonusInclusions={loyaltyRoomUpgradeBonusInclusions} />
                    </VerticalSpacer>
                  </LargeContentToggle>
                </TargetUpgradePackageInclusions>
              )}
              {loyaltyPackageUpgradePkg && (
                <TargetUpgradePackageInclusions className={clsx({ 'show': selectedTargetUpgradeType === 'package' })}>
                  <LargeContentToggle
                    maxHeight={INCLUSIONS_COLLAPSED_MAX_HEIGHT}
                    showMoreText={viewMoreInclusionsText}
                    showLessText={viewLessInclusionsText}
                    open={inclusionsOpen}
                    onToggle={onInclusionsToggle}
                  >
                    <VerticalSpacer gap={8}>
                      {!!loyaltyPackageUpgradeLuxPlusInclusions.length && (
                        <LuxPlusInclusions
                          luxPlusInclusions={loyaltyPackageUpgradeLuxPlusInclusions}
                          accommodationOffer={offer}
                          hideUpsellModal
                        />
                      )}
                      {showTasteOfGoldBenefit && <TasteOfGoldTileBenefitMessage />}
                      <InclusionsSection inclusions={loyaltyPackageUpgradeInclusions} bonusInclusions={loyaltyPackageUpgradeBonusInclusions} />
                    </VerticalSpacer>
                  </LargeContentToggle>
                </TargetUpgradePackageInclusions>
              )}
            </InclusionsContainer>
          )}
        </AccommodationSameRoomOptionTileDetails>

        <AccommodationSameRoomOptionTilePricing>
          {!children && availableForPurchase && <FlashHotelOptionPriceDetails packageView={packageView} roomsBeforeSurcharge={roomsBeforeSurcharge} />}
        </AccommodationSameRoomOptionTilePricing>

        <AccommodationSameRoomOptionTileCTA>
          {children}
          {!children && availableForPurchase && <FlashHotelOptionCTAs packageView={packageView} />}
        </AccommodationSameRoomOptionTileCTA>
      </AccommodationSameRoomOptionTile>
    </AccommodationSameRoomOptionTileSubgrid>
  )
}

type MappedProps = Pick<Props,
  | 'offer'
  | 'checkIn'
  | 'duration'
  | 'availableForPurchase'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  offer: state.offer as App.HotelOffer,
  checkIn: state.checkInDate,
  duration: state.duration,
  availableForPurchase: isAvailableForPurchase(state, appState),
}), FlashHotelRateOption)
